import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Brand } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { TranslationTag, Values } from '@swf/core/types/i18n';
import { emailTagTransform } from '@swf/core/utils/emailTagTransformer';

export interface Options extends Pick<TranslationTag, 'resolve'> {
  domain?: string;
  values?: Values;
}

const POKER_STARS_CASINO = 'PokerStars Casino';
const POKER_STARS_VEGAS = 'PokerStars Vegas';

const useTranslationValues = ({ resolve, domain, values: rawValues }: Options = {}): Values => {
  const appDomain = useSelector(select.site.getDomain);
  const brand = useSelector(select.site.getBrand);
  const accountBrand = brand === Brand.FoxBet ? 'User' : 'Stars';
  const brandName = useSelector(select.site.getBrandName);
  const starsDomain = useSelector(select.site.getStarsDomain);
  const vegasContent = useSelector(select.site.showVegasContent);
  const rewardsBrand = useSelector(select.rewards.getRewardsBrand);

  const values = useMemo(() => {
    const newValues = {
      appDomain,
      appStarsdomain: starsDomain,
      accountBrand,
      brandCoin: 'StarsCoin',
      brand:
        vegasContent && [Brand.StarsCasino, Brand.PokerStarsVegas].includes(brand)
          ? POKER_STARS_VEGAS
          : brand === Brand.StarsCasino
          ? POKER_STARS_CASINO
          : brandName,
      rewardsBrand,
      ...rawValues
    };

    if (resolve) {
      Object.entries(resolve).forEach(([key, resolved]) => {
        const { emailLink, emailText } = emailTagTransform(key, resolved, domain, starsDomain);
        if (emailText) {
          newValues[key] = emailText;
        }
        if (emailLink) {
          newValues[key] = emailLink;
        }
      });
    }
    return newValues;
  }, [
    accountBrand,
    appDomain,
    brand,
    brandName,
    domain,
    rawValues,
    resolve,
    starsDomain,
    vegasContent,
    rewardsBrand
  ]);

  return values;
};

export default useTranslationValues;
